import { useState, useEffect, useRef } from 'react';

const CTASection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);

    return () => observer.disconnect();
  }, []);

  const handleClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <section 
      ref={sectionRef}
      className="py-12 sm:py-16 md:py-20 lg:py-24 bg-gradient-to-br from-[#2B5BA6] via-[#234a8a] to-[#1e3d73] relative overflow-hidden"
    >
      {/* Animated Background Elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-10 left-10 w-64 h-64 bg-white rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-[#4A90E2] rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
        <div className={`transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
        }`}>
          {/* Icon */}
          <div className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 flex items-center justify-center bg-white/10 backdrop-blur-sm rounded-full mx-auto mb-5 sm:mb-6 lg:mb-8 animate-bounce">
            <i className="ri-rocket-line text-3xl sm:text-4xl lg:text-5xl text-white"></i>
          </div>

          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-3 sm:mb-4 lg:mb-6 px-4 leading-tight">
            Ready to Keep More Money?
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-white/90 mb-6 sm:mb-8 lg:mb-10 max-w-3xl mx-auto leading-relaxed px-4">
            Stop overpaying for roofing. Get your instant estimate now and connect with vetted pros who deliver quality work at fair prices — no middleman markups.
          </p>

          {/* CTA Button */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-stretch sm:items-center px-4">
            <button
              onClick={handleClick}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              className="group relative px-6 sm:px-8 lg:px-10 py-4 sm:py-4 lg:py-5 bg-white text-[#2B5BA6] text-base sm:text-lg font-bold rounded-xl hover:bg-gray-50 transition-all duration-300 shadow-2xl hover:shadow-white/20 whitespace-nowrap min-h-[52px] sm:min-h-[56px] transform hover:scale-105 active:scale-95 overflow-hidden flex items-center justify-center"
            >
              <span className="relative z-10 flex items-center justify-center">
                Get Instant Estimate Now
                <i className={`ri-arrow-right-line ml-2 text-xl transition-transform duration-300 ${
                  isHovered ? 'translate-x-2' : ''
                }`}></i>
              </span>
              <div className={`absolute inset-0 bg-gradient-to-r from-[#4A90E2] to-[#2B5BA6] transition-opacity duration-300 ${
                isHovered ? 'opacity-10' : 'opacity-0'
              }`}></div>
            </button>

            <a
              href="tel:+14793366968"
              className="inline-flex items-center gap-3 px-8 py-4 bg-white text-gray-900 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer text-lg font-semibold"
            >
              <i className="ri-phone-line text-2xl"></i>
              Call (479) 336-6968
            </a>
          </div>

          {/* Trust Indicators */}
          <div className={`mt-8 sm:mt-10 lg:mt-12 flex flex-col sm:flex-row flex-wrap justify-center items-center gap-4 sm:gap-6 lg:gap-8 transition-all duration-1000 delay-300 px-4 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            {[
              { icon: 'ri-flashlight-line', text: 'Instant Results' },
              { icon: 'ri-shield-check-line', text: 'Vetted Pros Only' },
              { icon: 'ri-hand-coin-line', text: 'No Middleman Fees' }
            ].map((item, index) => (
              <div 
                key={index}
                className="flex items-center space-x-2 text-white/90 transform hover:scale-110 transition-transform duration-300"
              >
                <div className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center flex-shrink-0">
                  <i className={`${item.icon} text-xl sm:text-2xl`}></i>
                </div>
                <span className="text-sm sm:text-base font-medium whitespace-nowrap">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTASection;
